<template>
  <div>
    <!-- SECTION EXPOSITION (quand homePage = false) -->
    <div v-if="!homePage" class="exhibition-wrapper">

      <!-- Header immersif avec la première image en fond -->
      <section class="immersive-header" :style="heroBackgroundStyle">
        <div class="header-overlay">
          <h1 class="expo-title">{{ name }}</h1>
          <div class="expo-subtitle">Exposition Virtuelle</div>
          <div class="expo-controls">
            <button class="control-button" @click="toggleViewMode">
              {{ viewMode === 'grid' ? 'Vue Mosaïque' : 'Vue Grille' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Galerie interactive avec deux modes -->
      <section id="gallery" ref="gallerySection" class="gallery-section">
        <!-- Vue Grille -->
        <div v-if="viewMode === 'grid'" class="gallery-grid">
          <div 
            v-for="(resource, index) in filteredResources" 
            :key="index"
            class="grid-item"
            @click="openLightbox(getOriginalIndex(index))"
          >
            <div class="grid-item-wrapper">
              <div class="grid-item-image">
                <img 
                  :src="resourceUrls[getOriginalIndex(index)]" 
                  alt="Resource Image" 
                  class="resource-image" 
                />
              </div>
              <div class="grid-item-overlay">
                <h3 class="item-title">{{ resource.title }}</h3>
                <p class="item-type">{{ getResourceType(resource) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Vue Mosaïque -->
        <div v-else class="gallery-mosaic">
          <div 
            v-for="(resource, index) in filteredResources" 
            :key="index"
            class="mosaic-item"
            :style="getMosaicStyle(index)"
            @click="openLightbox(getOriginalIndex(index))"
          >
            <div class="mosaic-item-wrapper">
              <img 
                :src="resourceUrls[getOriginalIndex(index)]" 
                alt="Resource Image" 
                class="mosaic-image" 
              />
              <div class="mosaic-overlay">
                <h3 class="mosaic-title">{{ resource.title }}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- Footer avec vagues (optionnel) -->
      <footer class="wave-footer">
        
        <div class="footer-content">
          <span>2023 ALPA TECHNOLOGIES</span>
          <span>Mentions Légales</span>
          <span>Fattu in Corsica</span>
        </div>
      </footer>
    </div>

    <!-- SECTION HOME (quand homePage = true) -->
    <div v-else class="home-section">
      <component
        :is="dynamicComponent"
        :exhibitIdProps="exhibitId"
        :homeImage="homeImage"
        @exit="exitHomePage"
      ></component>
    </div>

    <!-- LIGHTBOX IMMERSIVE -->
    <div
      v-if="selectedImageIndex !== null"
      class="lightbox"
      @click="closeLightbox"
    >
      <div class="lightbox-content" @click.stop>
        <!-- Image dans la lightbox -->
        <img
          :src="resourceUrls[selectedImageIndex]"
          alt="Full Image"
          class="lightbox-image"
        />
        
        <!-- Informations sur l'œuvre -->
        <div class="lightbox-info">
          <h2 class="lightbox-title">{{ resources[selectedImageIndex]?.title }}</h2>
          <p class="lightbox-description">{{ resources[selectedImageIndex]?.description }}</p>
        </div>
        
        <!-- Navigation -->
        <div class="lightbox-nav">
          <button 
            class="nav-button prev" 
            @click.stop="navigateLightbox(-1)" 
            :disabled="selectedImageIndex === 0"
          >
            &#10094;
          </button>
          <button 
            class="nav-button next" 
            @click.stop="navigateLightbox(1)" 
            :disabled="selectedImageIndex === resources.length - 1"
          >
            &#10095;
          </button>
        </div>
      </div>
      
      <!-- Bouton de fermeture -->
      <button class="lightbox-close" @click.stop="closeLightbox">X</button>
    </div>
  </div>
</template>

<script setup>
/* ---------------------
   IMPORTS
---------------------- */
import { ref, computed, onMounted, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import axios from "axios";

// Import des différents modèles (pour la home)
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";

/* ---------------------
   PROPS
---------------------- */
const props = defineProps({
  homePageProps: {
    type: Boolean,
    default: true,
  },
  exhibitId: {
    type: String,
    default: null,
  },
});

/* ---------------------
   STATES (data)
---------------------- */
const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);

const resources = ref([]);
const name = ref("");
const userName = ref("");
const homeImage = ref({});
const resourceUrls = ref([]);

// Contrôle de la page d'accueil
const homePage = ref(props.homePageProps);

// Modèle sélectionné
const selectedTemplate = ref("");

// Lightbox
const selectedImageIndex = ref(null);

// Mode d'affichage et recherche
const viewMode = ref('grid'); // 'grid' ou 'mosaic'
const searchQuery = ref('');

/* ---------------------
   COMPUTED
---------------------- */
/**
 * On utilise la première image comme image de fond
 * pour le header immersif (si on a une image)
 */
const heroBackgroundStyle = computed(() => {
  if (resourceUrls.value.length > 0) {
    return {
      backgroundImage: `url(${resourceUrls.value[0]})`,
    };
  }
  return {};
});

/* ---------------------
   MODELES DYNAMIQUES
---------------------- */
const modeleComponents = {
  Modele_1,
  Modele_2,
  Modele_3,
  Modele_4,
  Modele_5,
  Modele_6,
  Modele_7
};

const dynamicComponent = computed(() => {
  return modeleComponents[selectedTemplate.value] || null;
});

/**
 * Filtrer les ressources en fonction de la recherche
 */
const filteredResources = computed(() => {
  if (!searchQuery.value) return resources.value;
  
  const query = searchQuery.value.toLowerCase();
  return resources.value.filter(resource => 
    resource.title?.toLowerCase().includes(query) || 
    resource.description?.toLowerCase().includes(query)
  );
});

/**
 * Obtenir l'index original d'une ressource filtrée
 */
function getOriginalIndex(filteredIndex) {
  const filteredResource = filteredResources.value[filteredIndex];
  return resources.value.findIndex(r => r.id === filteredResource.id);
}

/**
 * Déterminer le type de ressource (video, image, etc.)
 */
function getResourceType(resource) {
  const name = resource.name?.toLowerCase() || '';
  if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi')) {
    return 'Vidéo';
  } else if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')) {
    return 'Image';
  } else if (name.endsWith('.pdf')) {
    return 'Document';
  }
  return 'Autre';
}

/**
 * Changer le mode d'affichage
 */
function toggleViewMode() {
  viewMode.value = viewMode.value === 'grid' ? 'mosaic' : 'grid';
}

/**
 * Style pour les éléments de la mosaïque
 */
function getMosaicStyle(index) {
  const sizes = ['small', 'medium', 'large'];
  const size = sizes[index % sizes.length];
  
  return {
    '--random-scale': size === 'small' ? '1' : size === 'medium' ? '1.5' : '2',
  };
}

/* ---------------------
   FONCTIONS (methods)
---------------------- */

// Récupère le nom de l'expo
async function getExhibitName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
    const response = await axios.get(url);
    name.value = response.data.exhibit_name;
  } catch (error) {
    console.error("Error getting exhibit name:", error);
  }
}

// Récupère le nom de l'utilisateur
async function getUserName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
    const response = await axios.get(url);
    userName.value = response.data.name; 
  } catch (error) {
    console.error(error);
  }
}

// Récupère l'image d'accueil
async function getHomeImage() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${exhibitParam}`;
    const response = await axios.get(url);
    homeImage.value = response.data[0];
  } catch (error) {
    console.log(error);
  }
}

// Récupère le modèle
async function getModel() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/model/${exhibitParam}`;
    const response = await axios.get(url);
    if (response.data.length > 0) {
      let modelName = response.data[0].model;
      // Correction
      modelName = modelName.replace(/è/g, 'e').replace(/\s+/g, '_');
      selectedTemplate.value = modelName; 
    }
  } catch (error) {
    console.log(error);
  }
}

// Récupère les ressources (métadonnées)
async function getResources() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;

    const resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
    const response = await axios.post(resourcesUrl, { exhibitId: exhibitParam });
    const allResources = response.data;

    const galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitParam}`;
    const galleryResponse = await axios.get(galleryUrl);
    const galleryResourceIds = galleryResponse.data.map(res => res.resource_id);

    resources.value = allResources.filter(resource =>
      galleryResourceIds.includes(resource.id)
    );
  } catch (error) {
    console.error("Error fetching resources:", error);
  }
}

// Récupère les URLs S3
async function fetchResources() {
  if (resources.value.length === 0) {
    await getResources();
  }
  const urls = resources.value.map(
    (r) => `${process.env.VUE_APP_BASEURL}/s3Resource/${r.name}`
  );
  try {
    const responses = await Promise.all(urls.map(url => axios.get(url)));
    resourceUrls.value = responses.map(r => r.data);
  } catch (error) {
    console.error("Erreur lors de la récupération des URLs", error);
  }
}

// Quitter la page d'accueil
function exitHomePage() {
  homePage.value = false;
}

// Ouvrir la lightbox
function openLightbox(index) {
  selectedImageIndex.value = index;
}

// Fermer la lightbox
function closeLightbox() {
  selectedImageIndex.value = null;
}

// Navigation dans la lightbox
function navigateLightbox(direction) {
  const newIndex = selectedImageIndex.value + direction;
  if (newIndex >= 0 && newIndex < resources.value.length) {
    selectedImageIndex.value = newIndex;
  }
}

/* ---------------------
   LIFECYCLE
---------------------- */
onMounted(async () => {
  await getHomeImage();
  await getResources();
  await fetchResources();
  await getExhibitName();
  await getUserName();
  await getModel();
});

// Références pour le défilement
const gallerySection = ref(null);
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@300;500;700&family=Playfair+Display:wght@400;700&display=swap");

/* Variables */
:root {
  --primary-color: #2c3e50;
  --accent-color: #e74c3c;
  --text-color: #333;
  --light-color: #ecf0f1;
  --dark-color: #2c3e50;
  --transition-speed: 0.5s;
}

/* Conteneur global */
.exhibition-wrapper {
  font-family: "Montserrat", sans-serif;
  color: var(--text-color);
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #f9f9f9;
}

/* Header immersif */
.immersive-header {
  width: 100%;
  height: 80vh;
  background-size: cover;
  background-position: center center;
  background-attachment: fixed;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.header-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.expo-title {
  font-family: "Playfair Display", serif;
  color: #fff;
  font-size: 5rem;
  text-transform: uppercase;
  letter-spacing: 5px;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.5);
  animation: fadeInDown 1.5s ease-out;
}

.expo-subtitle {
  color: #fff;
  font-size: 1.5rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 2rem;
  animation: fadeInUp 1.5s ease-out;
}

.expo-controls {
  display: flex;
  gap: 15px;
  margin-top: 20px;
  animation: fadeIn 2s ease-out;
}

.control-button {
  background: rgba(255,255,255,0.2);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  transition: background 0.3s;
}

.control-button:hover {
  background: rgba(255,255,255,0.3);
}

.search-container {
  position: relative;
}

.search-input {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 10px 15px;
  border-radius: 30px;
  width: 200px;
  font-family: "Montserrat", sans-serif;
}

.search-input::placeholder {
  color: rgba(255,255,255,0.7);
}

/* Barre de navigation */
.exhibit-nav {
  position: sticky;
  top: 0;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 100;
}

.nav-container {
  display: flex;
  justify-content: center;
  padding: 15px 0;
  max-width: 800px;
  margin: 0 auto;
}

.nav-item {
  background: none;
  border: none;
  color: var(--text-color);
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  padding: 10px 20px;
  margin: 0 10px;
  cursor: pointer;
  position: relative;
  transition: color 0.3s;
}

.nav-item:hover {
  color: var(--accent-color);
}

.nav-item.active {
  color: var(--accent-color);
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  width: 60%;
  height: 2px;
  background-color: var(--accent-color);
}

/* Galerie en grille */
.gallery-section {
  padding: 3rem 2rem;
  min-height: 50vh;
  background-color: white;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  max-width: 1400px;
  margin: 0 auto;
}

.grid-item {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
}

.grid-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.15);
}

.grid-item-wrapper {
  position: relative;
  overflow: hidden;
}

.grid-item-image {
  height: 250px;
  background: #f0f0f0;
}

.resource-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.grid-item:hover .resource-image {
  transform: scale(1.05);
}

.grid-item-overlay {
  padding: 15px;
  background: white;
  position: relative;
}

.item-title {
  font-family: "Playfair Display", serif;
  font-size: 1.2rem;
  margin-bottom: 5px;
  color: var(--dark-color);
}

.item-type {
  font-size: 0.8rem;
  color: var(--accent-color);
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Galerie en mosaïque */
.gallery-mosaic {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  max-width: 1400px;
  margin: 0 auto;
}

.mosaic-item {
  flex: 1 0 calc(33.333% - 15px);
  min-width: calc(33.333% - 15px);
  height: 300px;
  transform: scale(var(--random-scale, 1));
  margin: 10px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.mosaic-item:hover {
  z-index: 10;
  box-shadow: 0 15px 30px rgba(0,0,0,0.2);
}

.mosaic-item-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.mosaic-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.mosaic-item:hover .mosaic-image {
  transform: scale(1.05);
}

.mosaic-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  color: white;
  transform: translateY(100%);
  transition: transform 0.3s;
}

.mosaic-item:hover .mosaic-overlay {
  transform: translateY(0);
}

.mosaic-title {
  font-family: "Playfair Display", serif;
  font-size: 1.1rem;
  margin: 0;
}

/* Pas de résultats */
.no-results {
  text-align: center;
  padding: 3rem;
  color: #888;
  font-style: italic;
}

/* Section d'information */
.info-section {
  padding: 5rem 2rem;
  background-color: var(--light-color);
}

.info-container {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.info-title {
  font-family: "Playfair Display", serif;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--dark-color);
}

.info-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
  margin-bottom: 3rem;
}

.info-stats {
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-top: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 3rem;
  font-weight: 700;
  color: var(--accent-color);
}

.stat-label {
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--dark-color);
}

/* Footer avec vagues */
.wave-footer {
  position: relative;
  background: var(--dark-color);
  color: white;
  padding-top: 100px;
  margin-top: 3rem;
}

.wave-container {
  position: absolute;
  top: -100px;
  left: 0;
  width: 100%;
  height: 100px;
  overflow: hidden;
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg"><path fill="%232c3e50" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  background-size: 1440px 100px;
}

.wave1 {
  animation: wave 30s linear infinite;
  z-index: 3;
  opacity: 1;
}

.wave2 {
  animation: wave 15s linear reverse infinite;
  z-index: 2;
  opacity: 0.5;
}

.wave3 {
  animation: wave 20s linear infinite;
  z-index: 1;
  opacity: 0.2;
}

.footer-content {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 3rem 0;
  text-transform: uppercase;
  font-size: 0.8rem;
}

/* Home section */
.home-section {
  width: 100%;
  height: 100vh;
  background: #fff;
  position: relative;
}

/* LIGHTBOX IMMERSIVE */
.lightbox {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.lightbox-content {
  display: flex;
  flex-direction: column;
  width: 80%;
  max-width: 1200px;
  max-height: 90vh;
  background: rgba(255,255,255,0.05);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0,0,0,0.5);
  animation: fadeIn 0.3s ease-out;
}

.lightbox-image {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.lightbox-info {
  padding: 2rem;
  color: white;
  background: rgba(0,0,0,0.5);
}

.lightbox-title {
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  margin-bottom: 1rem;
}

.lightbox-description {
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.8;
}

.lightbox-nav {
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: space-between;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 2rem;
}

.nav-button {
  background: rgba(255,255,255,0.2);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  transition: background 0.3s;
}

.nav-button:hover {
  background: rgba(255,255,255,0.4);
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.lightbox-close {
  position: fixed;
  top: 20px;
  right: 30px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10000;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.3s;
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.4);
}

/* Animations */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes gallery-spin {
  from {
    transform: translateZ(-1000px) rotateY(0deg);
  }
  to {
    transform: translateZ(-1000px) rotateY(360deg);
  }
}

@keyframes wave {
  0% {
    background-position-x: 0;
  }
  100% {
    background-position-x: 1440px;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .expo-title {
    font-size: 3rem;
  }
  
  .expo-subtitle {
    font-size: 1rem;
  }
  
  .gallery-item {
    width: 250px;
    height: 350px;
    left: calc(50% - 125px);
    top: calc(50% - 175px);
  }
  
  .lightbox-content {
    width: 95%;
  }
  
  .lightbox-title {
    font-size: 1.5rem;
  }
  
  .footer-content {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
}
</style>
  
  